import { getViewerTimezone } from "@/utils/format";

// Controlled by an env var (VITE_USE_MOCK), not a hardcoded constant — on
// purpose. A hardcoded `true`/`false` here means switching between mock and
// real data requires editing code and getting that exact change committed
// and deployed, which is easy to lose track of (e.g. a local edit that
// never made it into what a hosting platform actually builds from). As an
// env var, mock-vs-real is controlled per-environment directly from your
// hosting dashboard — set VITE_USE_MOCK=false in Cloudflare Pages'
// environment variables and redeploy, no code change needed.
// Defaults to mock (true) when unset, so local dev without a .env file
// still works out of the box.
export const USE_MOCK = import.meta.env.VITE_USE_MOCK !== "false";

// This points at OUR proxy server (server/index.js), never at API-Football
// directly. The proxy is what actually holds the API-Football key — calling
// api-sports.io straight from the browser would expose the key to anyone who
// opens devtools. See server/README.md for setup.
export const API_PROXY_BASE_URL = import.meta.env.VITE_API_PROXY_URL ?? "http://localhost:8787/api";

// Every fixture-related request carries the viewer's timezone through to
// API-Football, which otherwise filters `date` against UTC — see
// getViewerTimezone()'s doc comment for why that causes matches to show up
// missing or on the wrong day compared to timezone-aware apps like FotMob.
function withTz(url: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}tz=${encodeURIComponent(getViewerTimezone())}`;
}

export const ENDPOINTS = {
  liveMatches: withTz(`${API_PROXY_BASE_URL}/fixtures/live`),
  scheduledMatches: (date: string) => withTz(`${API_PROXY_BASE_URL}/fixtures?date=${date}`),
  matchById: (id: string) => withTz(`${API_PROXY_BASE_URL}/fixtures/${id}`),
  statisticsForMatch: (id: string) => `${API_PROXY_BASE_URL}/fixtures/${id}/statistics`,
  lineupsForMatch: (id: string) => `${API_PROXY_BASE_URL}/fixtures/${id}/lineups`,
  eventsForMatch: (id: string) => `${API_PROXY_BASE_URL}/fixtures/${id}/events`,
  streamsForMatch: (id: string) => `${API_PROXY_BASE_URL}/streams/${id}`,
  search: (query: string) => withTz(`${API_PROXY_BASE_URL}/search?q=${encodeURIComponent(query)}`),
  leagues: `${API_PROXY_BASE_URL}/leagues`,
} as const;

// Client-side polling cadence for live data. The proxy server caches
// upstream API-Football responses for slightly less than this, so multiple
// browser tabs polling at this interval don't multiply your API quota usage.
export const LIVE_POLL_INTERVAL_MS = 15_000;
